import type {
  AntdTreeType,
  CategoryInterface,
  NomenclatureGroupInterface,
} from '@/types'
import getPathForSelectedTreeNode from '@/utils/tree/nodes/getPathForSelectedTreeNode'

export const getExpandedKeysForValue = (
  tree: NomenclatureGroupInterface[] | CategoryInterface[],
  selectedValue?: string
) => {
  if (!selectedValue) return []

  const path = getPathForSelectedTreeNode(tree, selectedValue)
  if (!path) return []

  const keys = path.map((item) =>
    'category' in item ? item.category.id : item.nomenclatureGroup.id
  )

  const selectedNode = path[path.length - 1]
  const hasChildren =
    ('category' in selectedNode &&
      (selectedNode.childCategories || []).length > 0) ||
    ('nomenclatureGroup' in selectedNode &&
      (selectedNode.childGroups || []).length > 0)

  return hasChildren ? keys : keys.slice(0, -1)
}

export const getExpandedKeysForSearchValue = (
  antdTree: AntdTreeType[],
  searchValue: string
) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase()
  if (!normalizedSearchValue) return []

  const expandedKeys = new Set<string>()

  const hasMatchingNode = (nodes: AntdTreeType[]): boolean => {
    return nodes.reduce<boolean>((hasMatchInCurrentLevel, node) => {
      const children = node.children || []
      const key = String(node.key || node.value || '')
      const nodeMatches = Boolean(
        node.filterProp?.toLowerCase().includes(normalizedSearchValue)
      )
      const childMatches = hasMatchingNode(children)

      if ((nodeMatches || childMatches) && children.length > 0 && key) {
        expandedKeys.add(key)
      }

      return hasMatchInCurrentLevel || nodeMatches || childMatches
    }, false)
  }

  hasMatchingNode(antdTree)

  return Array.from(expandedKeys)
}

export const filterAntdTree = (
  nodes: AntdTreeType[],
  normalizedQuery: string
): AntdTreeType[] => {
  const filtered: AntdTreeType[] = []

  for (const node of nodes) {
    const children = node.children
      ? filterAntdTree(node.children, normalizedQuery)
      : []
    const selfMatch = Boolean(
      node.filterProp?.toLowerCase().includes(normalizedQuery)
    )

    if (selfMatch || children.length > 0) {
      filtered.push({ ...node, children })
    }
  }

  return filtered
}

export const collectExpandedBranchKeys = (nodes: AntdTreeType[]): string[] => {
  const keys: string[] = []

  const walk = (list: AntdTreeType[]) => {
    for (const node of list) {
      if (node.children?.length) {
        keys.push(String(node.key))
        walk(node.children)
      }
    }
  }

  walk(nodes)
  return keys
}
